import * as Yup from 'yup';
import { useCallback, useEffect } from 'react';
import { useState } from 'react';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { Form, FormikProvider, useFormik } from 'formik';
// material
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Card,
  Grid,
  Stack,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputAdornment,
  Tabs,
  InputLabel,
  Typography,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Paper,
  CardHeader,
  Autocomplete
} from '@mui/material';
import { Tab } from '@mui/material';
import { axios } from 'utils/axios';
import ContactSites from 'pages/dashboard/contact-sites/ContactSites';
import { FormHelperText } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { ContactIcons } from 'utils/Icons/icons';
import InfoCard from 'components/common/InfoCard';

type ContactNewFormProps = {
  isEdit: boolean;
  isDetails: boolean;
  currentUser?: any;
};

export default function ContactCreateForm({ isEdit, isDetails, currentUser }: ContactNewFormProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const activeTab = queryParams.get('tab') || 'default';
  const { enqueueSnackbar } = useSnackbar();
  const [currentTab, setCurrentTab] = useState(0);
  const [councils, setCouncils] = useState([]);
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    if (activeTab === 'sites') {
      setCurrentTab(1);
    } else {
      setCurrentTab(0);
    }
  }, [activeTab]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  // Validation Schema
  const NewUserSchema = Yup.object().shape({
    externalRef: Yup.string().optional(),
    accountRef: Yup.string().required('Account Reference is required'),
    name: Yup.string().required('Name is required'),
    contactName: Yup.string().optional(),
    defaultCompanyId: Yup.string().required('Company is required'),
    // telephone: Yup.string()
    //   .matches(/^\+?[1-9]\d{1,14}$/, 'Invalid phone number')
    //   .required('Telephone is required'),
    email: Yup.string().email('Invalid email').optional(),
    address: Yup.object().shape({
      add_number: Yup.string().optional(),
      add_line1: Yup.string().optional(),
      add_line2: Yup.string().optional(),
      add_state: Yup.string().optional(),
      add_postcode: Yup.string().optional()
    }),

    councilArea: Yup.string().required('Council Area is required'),
    isOwner: Yup.boolean().optional(),
    isClient: Yup.boolean().optional(),
    isHaulier: Yup.boolean(),
    notes: Yup.string().nullable()
  });

  // Formik setup
  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    initialValues: {
      externalRef: currentUser?.ExternalRef || '',
      accountRef: currentUser?.AccountRef || '',
      name: currentUser?.Name || '',
      contactName: currentUser?.ContactName || '',
      telephone: currentUser?.Telephone || '',
      email: currentUser?.Email || '',
      address: {
        add_number: currentUser?.add_number || '',
        add_line1: currentUser?.add_line1 || '',
        add_line2: currentUser?.add_line2 || '',
        add_city: currentUser?.add_city || '',
        add_state: currentUser?.add_state || '',
        add_country: currentUser?.add_country || '',
        add_postcode: currentUser?.add_postcode || ''
      },
      councilArea: currentUser?.CouncilArea || '',
      defaultCompanyId: currentUser?.defaultCompanyId ?? null,
      isOwner: !!currentUser?.IsOwner || false,
      isClient: !!currentUser?.IsClient || false,
      isHaulier: currentUser?.IsHaulier || false,
      notes: currentUser?.Notes || ''
    },
    validationSchema: isDetails ? null : NewUserSchema,
    onSubmit: async (values, { setSubmitting }) => {
      if (!formik.isValid) {
        enqueueSnackbar('Please correct the errors before submitting.', { variant: 'error' });
        setSubmitting(false);
        return;
      }
      try {
        if (!isDetails) {
          console.log(values);
          const payload = {
            ExternalRef: values.externalRef,
            AccountRef: values.accountRef,
            Name: values.name,
            ContactName: values.contactName,
            defaultCompanyId: values.defaultCompanyId,
            Telephone: values.telephone,
            Email: values.email,
            add_number: values.address.add_number,
            add_line1: values.address.add_line1,
            add_line2: values.address.add_line2,
            add_state: values.address.add_state,
            add_postcode: values.address.add_postcode,
            Postcode: values.address.add_postcode,
            CouncilArea: values.councilArea,
            SourcePopulate: values.notes,
            Notes: values.notes,
            IsOwner: values.isOwner,
            IsClient: values.isClient,
            IsHaulier: values.isHaulier
          };

          const response = isEdit
            ? await axios.put(`/rest/api/contacts/info/${currentUser.Id}`, payload)
            : await axios.post('/rest/api/contacts/info', payload);

          enqueueSnackbar(!isEdit ? 'Create success' : 'Update success', { variant: 'success' });
          navigate('/dashboard/contacts');
        }
      } catch (error) {
        console.error(error);
        enqueueSnackbar('An error occurred while submitting the form', { variant: 'error' });
      } finally {
        setSubmitting(false);
      }
    }
  });

  const { errors, touched, handleSubmit, isSubmitting, getFieldProps } = formik;

  const fetchAllCouncils = async () => {
    const response = await axios.get('/rest/api/council/area');
    const allCouncils = response.data.data || [];

    setCouncils(allCouncils?.map((el: any) => ({ value: el.CouncilArea, id: el.Id })));
  };

  const fetchAllCompanies = async () => {
    const response = await axios.get('/rest/api/company/dropdown');
    const allCompanies = response.data.data || [];

    setCompanies(allCompanies?.map((el: any) => ({ value: el.name, id: el.id })));
  };

  useEffect(() => {
    fetchAllCouncils();
    fetchAllCompanies();
  }, []);

  console.log(errors, 'This is the errors');
  return (
    <FormikProvider value={formik}>
      {isDetails ? (
        <Box>
          <Grid container spacing={3} sx={{ mb: 3 }} alignItems="center">
            {[
              { label: 'Name', value: formik.values.name || 'N/A' },
              { label: 'Telephone', value: formik.values.telephone || 'N/A' },
              { label: 'Email', value: formik.values.email || 'N/A' },
              { label: 'Council Area', value: formik.values.councilArea || 'N/A' }
            ].map((item, index) => (
              <Grid item xs={12} sm={6} md={6} key={index}>
                <Card
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 3,
                    maxHeight: 100
                  }}
                >
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle2">{item.label}</Typography>
                    <Typography variant="body2">{item.value}</Typography>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Tabs value={currentTab} onChange={handleTabChange} sx={{ pb: '20px' }}>
            <Tab label="Details" />
            <Tab label="Sites" />
          </Tabs>

          {currentTab === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card sx={{ p: 3, boxShadow: 3 }}>
                  {/* General Information Section */}
                  <CardHeader title="General Information" />
                  <Stack spacing={3} padding={3}>
                    <Grid container spacing={2}>
                      {[
                        {
                          icon: ContactIcons.link,
                          title: 'External Reference',
                          content: formik.values.externalRef
                        },
                        {
                          icon: ContactIcons.assignment,
                          title: 'Account Reference',
                          content: formik.values.accountRef
                        },
                        {
                          icon: ContactIcons.locationCity,
                          title: 'Council Area',
                          content: formik.values.councilArea
                        }
                      ].map(({ icon, title, content }, idx) => (
                        <Grid item xs={12} sm={6} key={idx}>
                          <InfoCard icon={icon} title={title} content={content} />
                        </Grid>
                      ))}
                    </Grid>
                  </Stack>

                  {/* Contact Details Section */}
                  <CardHeader title="Contact Details" />
                  <Stack spacing={3} padding={3}>
                    <Grid container spacing={2}>
                      {[
                        {
                          icon: ContactIcons.person,
                          title: 'Name',
                          content: formik.values.name
                        },
                        {
                          icon: ContactIcons.contactPage,
                          title: 'Contact Name',
                          content: formik.values.contactName
                        },
                        {
                          icon: ContactIcons.call,
                          title: 'Telephone',
                          content: formik.values.telephone
                        },
                        {
                          icon: ContactIcons.email,
                          title: 'Email',
                          content: formik.values.email
                        }
                      ].map(({ icon, title, content }, idx) => (
                        <Grid item xs={12} sm={6} key={idx}>
                          <InfoCard icon={icon} title={title} content={content} />
                        </Grid>
                      ))}
                    </Grid>
                  </Stack>

                  {/* Address Information Section */}

                  <CardHeader title="Address Information" />
                  <Stack spacing={3} padding={3}>
                    <Grid container spacing={2}>
                      {[
                        {
                          icon: ContactIcons.house,
                          title: 'House Number',
                          content: formik.values.address?.add_number
                        },
                        {
                          icon: ContactIcons.place,
                          title: 'Address Line 1',
                          content: formik.values.address?.add_line1
                        },
                        {
                          icon: ContactIcons.place,
                          title: 'Address Line 2',
                          content: formik.values.address?.add_line2
                        },
                        {
                          icon: ContactIcons.pinDrop,
                          title: 'PostCode',
                          content: formik.values.address?.add_postcode
                        },
                        {
                          icon: ContactIcons.public,
                          title: 'State',
                          content: formik.values.address?.add_state
                        },
                        {
                          icon: ContactIcons.place,
                          title: 'City',
                          content: formik.values.address?.add_city
                        },
                        {
                          icon: ContactIcons.flag,
                          title: 'Country',
                          content: formik.values.address?.add_country
                        }
                      ].map(({ icon, title, content }, idx) => (
                        <Grid item xs={12} sm={6} key={idx}>
                          <InfoCard icon={icon} title={title} content={content} />
                        </Grid>
                      ))}
                    </Grid>
                  </Stack>

                  {/* Additional Details Section */}
                  <CardHeader title="Additional Details" />
                  <Stack spacing={3} padding={3}>
                    <Grid container spacing={2}>
                      {[
                        {
                          icon: ContactIcons.checkCircle,
                          title: 'Owner',
                          content: formik.values.isOwner ? 'Yes' : 'No'
                        },
                        {
                          icon: ContactIcons.group,
                          title: 'Client',
                          content: formik.values.isClient ? 'Yes' : 'No'
                        },
                        {
                          icon: ContactIcons.group,
                          title: 'Haulier',
                          content: formik.values.isHaulier ? 'Yes' : 'No'
                        },
                        {
                          icon: ContactIcons.note,
                          title: 'Notes',
                          content: formik.values.notes,
                          fullWidth: true
                        }
                      ].map(({ icon, title, content, fullWidth }, idx) => (
                        <Grid item xs={12} sm={fullWidth ? 12 : 6} key={idx}>
                          <InfoCard icon={icon} title={title} content={content} />
                        </Grid>
                      ))}
                    </Grid>
                  </Stack>
                </Card>
              </Grid>
            </Grid>
          )}

          {currentTab === 1 && <ContactSites />}
        </Box>
      ) : (
        <Form noValidate onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card sx={{ p: 3 }}>
                <Stack spacing={3}>
                  {/* External Reference and Account Reference */}
                  <Typography variant="h6" gutterBottom>
                    General Information
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <TextField
                      fullWidth
                      label="External Reference"
                      {...getFieldProps('externalRef')}
                      error={Boolean(touched.externalRef && errors.externalRef)}
                      helperText={touched.externalRef && errors.externalRef}
                    />
                    <TextField
                      fullWidth
                      label="Account Reference*"
                      {...getFieldProps('accountRef')}
                      error={Boolean(touched.accountRef && errors.accountRef)}
                      helperText={touched.accountRef && errors.accountRef}
                    />
                    <FormControl fullWidth>
                      <Autocomplete
                        options={councils}
                        getOptionLabel={(option: { value: string; id: number }) => option.value}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                        renderOption={(props, option: any) => (
                          <li {...props} key={`${option.id}-${option.value}`}>
                            <div>
                              <strong>{option?.value || 'N/A'}</strong>
                            </div>
                          </li>
                        )}
                        onChange={(event, newValue) => {
                          formik.setFieldValue('councilArea', newValue ? newValue.value : '');
                        }}
                        value={
                          councils?.find(
                            (option: { value: string; id: number }) =>
                              option.value === formik.values.councilArea
                          ) || null
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Council Area*"
                            error={Boolean(touched.councilArea && errors.councilArea)}
                            helperText={touched.councilArea && errors.councilArea}
                          />
                        )}
                      />
                    </FormControl>
                  </Stack>

                  {/* Contact Name and Name */}
                  <Typography variant="h6" gutterBottom>
                    Contact Details
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <TextField
                      fullWidth
                      label="Name*"
                      {...getFieldProps('name')}
                      error={Boolean(touched.name && errors.name)}
                      helperText={touched.name && errors.name}
                    />
                    <TextField
                      fullWidth
                      label="Contact Name"
                      {...getFieldProps('contactName')}
                      error={Boolean(touched.contactName && errors.contactName)}
                      helperText={touched.contactName && errors.contactName}
                    />
                    <FormControl fullWidth>
                      <Autocomplete
                        options={companies}
                        getOptionLabel={(option: { value: string; id: number }) => option.value}
                        isOptionEqualToValue={(option, value) => option.id === value?.id}
                        renderOption={(props, option: any) => (
                          <li {...props} key={`${option.id}-${option.value}`}>
                            <div>
                              <strong>{option?.value || 'N/A'}</strong>
                            </div>
                          </li>
                        )}
                        onChange={(event, newValue) => {
                          console.log(newValue, 'This is the new Value');
                          formik.setFieldValue('defaultCompanyId', newValue ? newValue.id : '');
                        }}
                        value={
                          companies.find(
                            (option: any) => option.id === formik.values.defaultCompanyId
                          ) ?? null
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Company*"
                            error={Boolean(touched.defaultCompanyId && errors.defaultCompanyId)}
                            helperText={touched.defaultCompanyId && errors.defaultCompanyId}
                          />
                        )}
                      />
                    </FormControl>
                  </Stack>
                  <Stack direction="row" spacing={2}>
                    <TextField
                      fullWidth
                      label="Telephone"
                      {...getFieldProps('telephone')}
                      error={Boolean(touched.telephone && errors.telephone)}
                      helperText={touched.telephone && errors.telephone}
                    />
                    <TextField
                      fullWidth
                      label="Email"
                      {...getFieldProps('email')}
                      error={Boolean(touched.email && errors.email)}
                      helperText={touched.email && errors.email}
                    />
                  </Stack>

                  {/* Address Fields */}
                  <Typography variant="h6" gutterBottom>
                    Address Information
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      fullWidth
                      label="House Number"
                      InputLabelProps={{ shrink: true }}
                      {...getFieldProps('address.add_number')}
                      error={Boolean(touched.address?.add_number && errors.address?.add_number)}
                      helperText={touched.address?.add_number && errors.address?.add_number}
                    />
                    <TextField
                      label="Address Line 1"
                      {...getFieldProps('address.add_line1')}
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      error={Boolean(touched.address?.add_line1 && errors.address?.add_line1)}
                      helperText={touched.address?.add_line1 && errors.address?.add_line1}
                    />
                    <TextField
                      label="Address Line 2"
                      {...getFieldProps('address.add_line2')}
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      error={Boolean(touched.address?.add_line2 && errors.address?.add_line2)}
                      helperText={touched.address?.add_line2 && errors.address?.add_line2}
                    />
                  </Stack>
                  <Stack direction="row" spacing={2}>
                    <TextField
                      fullWidth
                      label="Post Code"
                      {...getFieldProps('address.add_postcode')}
                      InputLabelProps={{ shrink: true }}
                      error={Boolean(touched.address?.add_postcode && errors.address?.add_postcode)}
                      helperText={touched.address?.add_postcode && errors.address?.add_postcode}
                    />
                  </Stack>

                  {/* Additional Details */}
                  <Typography variant="h6" gutterBottom>
                    Classification
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <FormControl component="fieldset" fullWidth>
                      <FormLabel component="legend">Owner</FormLabel>
                      <RadioGroup
                        row
                        {...getFieldProps('isOwner')}
                        name="isOwner"
                        value={formik.values.isOwner} // Keep value as boolean
                        onChange={(e) => formik.setFieldValue('isOwner', e.target.value === 'true')}
                      >
                        <FormControlLabel value={true} control={<Radio />} label="Yes" />
                        <FormControlLabel value={false} control={<Radio />} label="No" />
                      </RadioGroup>

                      {touched.isOwner && errors.isOwner && (
                        <FormHelperText error>{errors.isOwner}</FormHelperText>
                      )}
                    </FormControl>
                    <FormControl component="fieldset" fullWidth>
                      <FormLabel component="legend">Client</FormLabel>
                      <RadioGroup
                        row
                        {...getFieldProps('isClient')}
                        name="isClient"
                        value={formik.values.isClient} // Keep value as boolean
                        onChange={(e) =>
                          formik.setFieldValue('isClient', e.target.value === 'true')
                        }
                      >
                        <FormControlLabel value={true} control={<Radio />} label="Yes" />
                        <FormControlLabel value={false} control={<Radio />} label="No" />
                      </RadioGroup>

                      {touched.isClient && errors.isClient && (
                        <FormHelperText error>{errors.isClient}</FormHelperText>
                      )}
                    </FormControl>
                    <FormControl component="fieldset" fullWidth>
                      <FormLabel component="legend">Haulier</FormLabel>
                      <RadioGroup
                        row
                        {...getFieldProps('isHaulier')}
                        name="isHaulier"
                        value={formik.values.isHaulier ? 'true' : 'false'} // Ensure boolean to string conversion
                        onChange={(e) =>
                          formik.setFieldValue('isHaulier', e.target.value === 'true')
                        }
                      >
                        <FormControlLabel value="true" control={<Radio />} label="Yes" />
                        <FormControlLabel value="false" control={<Radio />} label="No" />
                      </RadioGroup>
                      {touched.isHaulier && errors.isHaulier && (
                        <FormHelperText error>{errors.isHaulier}</FormHelperText>
                      )}
                    </FormControl>
                  </Stack>

                  {/* Classification Fields */}
                  <Typography variant="h6" gutterBottom>
                    Additional Details
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <TextField
                      fullWidth
                      label="Notes"
                      multiline
                      rows={4} // Adjust the number of rows as needed
                      {...getFieldProps('notes')}
                      error={Boolean(touched.notes && errors.notes)}
                      helperText={touched.notes && errors.notes}
                    />
                  </Stack>

                  {/* Submit Button */}
                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                    <LoadingButton
                      type="submit"
                      variant="contained"
                      loading={isSubmitting}
                      disabled={
                        !isEdit && // If it's not in edit mode
                        ((!isDetails && !formik.isValid) || // If validation is required and form is invalid
                          formik.isSubmitting ||
                          Object.keys(formik.errors).length > 0)
                      }
                    >
                      {!isEdit ? 'Create Contact' : 'Save Changes'}
                    </LoadingButton>
                  </Box>
                </Stack>
              </Card>
            </Grid>
          </Grid>
        </Form>
      )}
    </FormikProvider>
  );
}
